import { useState } from 'react';
import { History, MicVocal, Upload } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils/cn';
import { useTtsAccess } from '@/features/tts-flow/api';
import { SynthesizePanel } from './SynthesizePanel';
import { TtsHistoryPanel } from './TtsHistoryPanel';
import { UploadAudioPanel } from './UploadAudioPanel';

type Tab = 'upload' | 'synthesize' | 'history';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: Tab;
}

const TABS: { id: Tab; label: string; description: string; icon: React.ComponentType<{ className?: string }> }[] = [
  {
    id: 'upload',
    label: 'Upload',
    description: 'Audio file',
    icon: Upload,
  },
  {
    id: 'synthesize',
    label: 'Synthesize',
    description: 'From text',
    icon: MicVocal,
  },
  {
    id: 'history',
    label: 'History',
    description: 'Previous renders',
    icon: History,
  },
];

export function AddPromptSheet({
  open,
  onOpenChange,
  defaultTab = 'upload',
}: Props) {
  const [tab, setTab] = useState<Tab>(defaultTab);
  // No TTS module → no Synthesize / History (TTS-only) tabs.
  const hasTts = useTtsAccess().reason !== 'no-module';
  const tabs = hasTts ? TABS : TABS.filter((t) => t.id === 'upload');
  const activeTab: Tab = tabs.some((t) => t.id === tab) ? tab : 'upload';

  const close = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Add Voice File</SheetTitle>
          <SheetDescription>
            {hasTts
              ? 'Bring audio into the library — upload an existing file, synthesize one from text, or resume a previous TTS render.'
              : 'Bring audio into the library by uploading an existing file.'}
          </SheetDescription>
        </SheetHeader>

        {/* Segmented tab bar */}
        {tabs.length > 1 && (
        <div className="mt-4 grid grid-cols-3 gap-1 rounded-lg border border-border bg-muted/30 p-1">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={cn(
                  'flex flex-col items-center gap-0.5 rounded-md px-2 py-2 text-center transition-all',
                  active
                    ? 'bg-card shadow-sm ring-1 ring-border'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <div className="flex items-center gap-1.5">
                  <Icon
                    className={cn(
                      'h-3.5 w-3.5',
                      active ? 'text-primary' : 'text-muted-foreground',
                    )}
                  />
                  <span className="text-xs font-semibold">{t.label}</span>
                </div>
                <span className="text-[10px] text-muted-foreground">
                  {t.description}
                </span>
              </button>
            );
          })}
        </div>
        )}

        <div className="mt-4">
          {activeTab === 'upload' && <UploadAudioPanel onSuccess={close} />}
          {activeTab === 'synthesize' && <SynthesizePanel onSuccess={close} />}
          {activeTab === 'history' && <TtsHistoryPanel />}
        </div>
      </SheetContent>
    </Sheet>
  );
}
